import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
// eslint-disable-next-line no-unused-vars
import { Bar as BarJS } from 'chart.js/auto'
import { Bar } from 'react-chartjs-2'
import { YDivider, XDivider, useTheme } from 'irmlib/styles'
import { Label, ContainedButton } from 'irmlib/components/Controls'
import { useConstructor } from 'irmlib/hooks'
import { uiGet } from 'irmlib/http'
import { useTranslation } from 'react-i18next'
import { BaseCard } from './BaseCard'
import { isAdmin, isSupervisor, isSuperUser } from '../../../../common/functions'
import { keyStrokePageInfo } from '../../../../services/reports/jobStatistic'
import { getUser } from '../../../../redux/global'

const titleHeight = '50px'
const linksCardBodyContainerWidth = '180px'

const VisualReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const VisualReportTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  background-color: ${props => props.theme.palette.dashboardReportTitleBackground};
  width: 100%;
  height: ${titleHeight};
  align-items: center;
  padding: 0px 20px;
`

const ReportsCardBodyContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  flex-wrap: wrap;
  padding-bottom: 20px;
  padding-top: 20px;
`

const Title = styled.div`
`

const LeftTitle = styled(Title)`
  font-weight: bold;
`

const ReportsCardBodyVisualReportContainer = styled.div`
  width: 48%;
`

const ReportsCardButtonContainer = styled.div`
  width: 100%;
  display:flex;
  margin: 10%;
  justify-content: center;
`

const ReportViewButton = styled(ContainedButton)`
  width: ${linksCardBodyContainerWidth};
`

const VisualReportTitle = props => {
  const theme = useTheme()
  return (
    <VisualReportTitleContainer theme={theme}>
      <LeftTitle>
        <Label>
          {props.leftTitle}
        </Label>
      </LeftTitle>
      <Title>
        <Label>
          dashBoard.last7BusinessDays
        </Label>
      </Title>
    </VisualReportTitleContainer>
  )
}

VisualReportTitle.propTypes = {
  leftTitle: PropTypes.string,
}

const ReportGraph = props => {
  const data = useRef([])
  const theme = useTheme()

  useConstructor(() => {
    data.current = {
      labels: props.data.map(k => k.label),
      datasets: [
        {
          data: props.data.map(d => d.value),
          backgroundColor: theme.palette.primary.main,
        }
      ]
    }
  })

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      }
    }
  }

  return (
    <Bar data={data.current} options={options} />
  )
}

ReportGraph.propTypes = {
  data: PropTypes.array,
}

const VisualReport = props => {
  return (
    <VisualReportContainer>
      <VisualReportTitle leftTitle={props.leftTitle} />
      <YDivider height='10px' />
      {props.data && <ReportGraph data={props.data} />}
    </VisualReportContainer>
  )
}

VisualReport.propTypes = {
  data: PropTypes.array,
  leftTitle: PropTypes.string,
}

const ReportsCardBody = props => {
  const { t } = useTranslation()
  let kphByDay
  let filesByDay
  if (props.data !== null) {
    kphByDay = Object.keys(props.data).sort((a1, a2) => a1.localeCompare(a2)).map(key => {
      return {
        label: props.data[key].date,
        value: props.data[key].kph
      }
    })
    
    
    filesByDay = Object.keys(props.data).sort((a1, a2) => a1.localeCompare(a2)).map(key => {
      return {
        label: props.data[key].date,
        value: props.data[key].filesByDay
      }
    })
  }
  return (
    <ReportsCardBodyContainer>
      <ReportsCardBodyVisualReportContainer>
        <VisualReport 
          data={kphByDay}
          leftTitle={isAdmin() || isSupervisor() || isSuperUser() ? 'dashBoard.kph' : 'dashBoard.myKph'}
        />
      </ReportsCardBodyVisualReportContainer>
      <XDivider width='4%' />
      <ReportsCardBodyVisualReportContainer>
        <VisualReport 
          data={filesByDay}
          leftTitle={isAdmin() || isSupervisor() || isSuperUser() ? 'dashBoard.files' : 'dashBoard.myFiles'}
      />
      </ReportsCardBodyVisualReportContainer>
      {!props.data && <ReportsCardButtonContainer>
        <ReportViewButton onClick={async() => {
          await props.fetchData() 
        }}>{t('buttons.viewReports')}</ReportViewButton>
      </ReportsCardButtonContainer>}
    </ReportsCardBodyContainer>
  )
}

ReportsCardBody.propTypes = {
  data: PropTypes.object,
  fetchData: PropTypes.func
}

const ReportsCard = () => {
  const [data, setData] = useState(null)

  const reportsService = async () => {
    const { district } = getUser()
    const data = await keyStrokePageInfo(district)
    /* 
    // mock data for testing
    data['1stDay'].kph = 10000
    data['1stDay'].filesByDay = 10
    data['2ndDay'].kph = 10100
    data['2ndDay'].filesByDay = 11
    data['3rdDay'].kph = 10200
    data['3rdDay'].filesByDay = 12
    data['4thDay'].kph = 10300
    data['4thDay'].filesByDay = 13
    data['5thDay'].kph = 10400
    data['5thDay'].filesByDay = 14
    data['6thDay'].kph = 10500
    data['6thDay'].filesByDay = 15
    data['7thDay'].kph = 10600
    data['7thDay'].filesByDay = 16
    */
    return data
  }

  const fetchData = async () => {
    const data = await uiGet({ service: reportsService })
    setData(data)
  }

  return (
    <BaseCard
      noHeight
      id='ReportssCard'
      body={<ReportsCardBody data={data} fetchData={fetchData} />} 
      title='dashBoard.reports' 
    />
  )
}

export default ReportsCard